// İSG Portal — Taşeron Self-Service (Supplier Self-Service) API çağrıları.
// **Taşeron principal tarafı** (supplier login §5.4 sonrası). Taşeron personeli, aktif
// firmadaki **kendi** taşeron firmasını görür + uygunluk belgelerini yönetir. Ana firma
// tarafı (§13.1–13.5) AYRI yetki modülüdür (`contractors`) ve web-expert'e aittir
// (contractors.ts) — bu istemci YALNIZCA supplier self-service içindir.
//
// KAYNAK: api-contract.md §13.6 (Supplier Self-Service — `[ModuleAction("supplier-contractor", …)]`).
// Backend kanonu; burada **varsayım YOK** — endpoint yolları, istek/yanıt alanları ve
// kimlik kuralları (Result unwrap `value`; Create → `value.uuid` + 201) doğrudan
// sözleşmedendir. Serileştirme camelCase'dir (§1). DTO tipleri contractors.ts ile ortaktır
// (ContractorDto / ContractorDocumentDto / ContractorPersonnelDto) — sözleşme aynı shape'i
// paylaşır (§13 DTO'lar) → yeniden tanımlanmaz, import edilir.
//
// **Scope izolasyonu (KRİTİK — §13.6).** Contractor kimliği **istemciden ASLA alınmaz** —
// aktif firmadaki `SupplierUserCompany.ContractorId` bağından + SupplierScopeValidator
// (§4b) ile sunucu çözer. Supplier **yalnız kendi contractor'ına** erişir; başka
// contractor/firma verisi hiçbir koşulda dönmez. Bağ yoksa (`ContractorId` null) →
// `404 CONTRACTOR_NOT_FOUND`; aktif firmaya atama yoksa → `403 COMPANY_NOT_ACCESSIBLE`.
// İstemci bu yüzden hiçbir uçta firma/contractor kimliği GÖNDERMEZ.
//
// **Uygunluk belgesi = §8 Storage link'i.** Belge dosyası önce §8 akışıyla yüklenir
// (documents.ts: `uploadDocument` → presigned PUT → confirm → `documentUuid`), sonra
// bu modüle `linkSupplierDocument` ile linklenir. İndirme yine §8 `documentsApi.getDocument`.

import { api } from './client';
import type {
  ActivationLinkResult,
  ContractorAccessLevel,
  ContractorDocumentDto,
  ContractorDocumentLinkRequest,
  ContractorDto,
  ContractorPersonnelDto,
  CreatePersonnelRequest,
  CreatePersonnelResult,
  InviteChannel,
  PersonnelDocumentDto,
  PersonnelDocumentLinkRequest,
  SendActivationLinkRequest,
} from './contractors';
import type { Empty } from './envelope';
import type {
  VehicleDocumentDto,
  VehicleDocumentUploadRequest,
  VehicleWriteRequest,
} from './permits';
import type { ContractorVehicleDto } from './vehicles';

// Ortak DTO/istek tipleri contractors.ts kanonundandır (§13 aynı shape) — yeniden
// dışa aktarılır ki web-supplier tek modülden (supplierContractorApi) import edebilsin.
export type {
  ComplianceStatus,
  ContractorAccessLevel,
  ContractorComplianceDto,
  ContractorDocumentDto,
  ContractorDocumentLinkRequest,
  ContractorDto,
  ContractorPersonnelDto,
  ContractorPersonnelKind,
  ContractorPersonnelStatus,
  ContractorStatus,
  HazardClass,
  InviteChannel,
  InviteDispatch,
  PersonnelDocumentDto,
  PersonnelDocumentLinkRequest,
} from './contractors';

// ============================================================================
// 13.6 Supplier Self-Service (`[ModuleAction("supplier-contractor", …)]`)
// ============================================================================

/**
 * `GET /supplier/contractor` (`get`) — supplier'ın aktif firmadaki **kendi** taşeron
 * firması (uygunluk özetiyle). Contractor kimliği token/bağdan çözülür — istemci
 * GÖNDERMEZ. Bağ yoksa → `404 CONTRACTOR_NOT_FOUND`; aktif firmaya atama yoksa →
 * `403 COMPANY_NOT_ACCESSIBLE`.
 */
export function getSupplierContractor() {
  return api.get<ContractorDto>('supplier/contractor');
}

/**
 * `GET /supplier/contractor/documents` (`get`) — kendi taşeronunun uygunluk belgeleri
 * (tür + `documentUuid` + geçerlilik + §8 `fileName`). Bağ yoksa → `404 CONTRACTOR_NOT_FOUND`.
 * Sayfasız dizi (zarf `value`).
 */
export function listSupplierContractorDocuments() {
  return api.get<ContractorDocumentDto[]>('supplier/contractor/documents');
}

/**
 * `POST /supplier/contractor/documents` (`update`) — §8'de confirm edilmiş dosyayı **kendi**
 * taşeronuna link'ler (self-service); yeni kimlik `value.uuid` (201). `documentUuid`
 * confirmed + kendi firmasına ait olmalı (yoksa `404 DOCUMENT_NOT_FOUND`); `type`
 * tanınmaz / `documentUuid` boş → `400 VALIDATION_FAILED`.
 */
export function linkSupplierDocument(body: ContractorDocumentLinkRequest) {
  return api.post<ContractorDocumentDto>('supplier/contractor/documents', { json: body });
}

/**
 * `DELETE /supplier/contractor/documents/{linkUuid}` (`update`) — kendi taşeronunun
 * bir belgesini **soft-delete** eder. Belge yok / kendi taşeronuna ait değil →
 * `404 CONTRACTOR_DOCUMENT_NOT_FOUND`.
 *
 * ⚠️ Silme hedefi **link kaydının kendi `uuid`'idir** (`ContractorDocumentDto.uuid` ← entity.Uuid),
 * §8 storage `documentUuid`'i **DEĞİL** (backend `d => d.Uuid == …` ile çözer; ikisi ayrı GUID).
 * §8 `documentUuid` gönderilirse her silme `404 CONTRACTOR_DOCUMENT_NOT_FOUND` döner. İndirme ise
 * §8 presigned GET ile `documentUuid` üzerinden yapılır.
 */
export function deleteSupplierDocument(linkUuid: string) {
  return api.delete<Empty>(`supplier/contractor/documents/${linkUuid}`);
}

/**
 * `GET /supplier/contractor/personnel` (`get`) — kendi taşeron firmasının personeli: aynı
 * contractor'a bağlı **aktive `SupplierUser`'lar + bekleyen davetler** (§13.3 ile aynı
 * `ContractorPersonnelDto` şekli — `kind`/`status`/`accessLevel`; **bu yüzeyde `tcKimlikNo`
 * doludur** — ana firma yüzeyi §13.3'te bilerek `null`, bkz. DTO notu). Bağ yoksa →
 * `404 CONTRACTOR_NOT_FOUND`. Sayfasız dizi. Yeni personel
 * `createSupplierPersonnel` (aşağıda) ile oluşturulur; var olan
 * **aktive** bir kaydı düzenlemek/firmadan çıkarmak için aşağıdaki **S26** tekil uçlarına bakın
 * (`updateSupplierContractorPersonnel`/`deleteSupplierContractorPersonnel`). Kişi çekmecesi (GL5)
 * bu listenin satırından beslenir (satır `tcKimlikNo` + `isActive` taşır) — tekil `GET` çağrılmaz.
 */
export function listSupplierContractorPersonnel() {
  return api.get<ContractorPersonnelDto[]>('supplier/contractor/personnel');
}

/**
 * Personel profili düzenleme isteği — api-contract §13.6 (S26, GL29,
 * `PUT /supplier/contractor/personnel/{userUuid}`). Form, Personelim listesinin satırından
 * (`listSupplierContractorPersonnel`) doldurulur. `accessLevel`/`isActive` her istekte gönderilir;
 * `firstName`/`lastName` `null` (ya da anahtar yok) = değişmez, dize = yaz, boş/whitespace dize →
 * `400 VALIDATION_FAILED` (`FIELD_EMPTY`). İstemci `null`'ı yalnız kayıtlı ad boşken alan boş
 * bırakıldığında gönderir; dolu ad boşaltılamaz (form engeller).
 *
 * `tcKimlikNo` **üç durumludur** (GL29, §13.3 ile aynı): anahtar yok = değişmez · `null` =
 * temizle · dize = yaz. İstemci (`apps/web-supplier` `usePersonnelClient`) anahtarı YALNIZ
 * kullanıcı boş bir T.C.'ye değer yazdığında koyar — bayat bir liste satırı başka bir yazarın (ör.
 * ana firma uzmanı) kaydettiği T.C.'yi böylece sessizce silemez; `null` (temizle) bu arayüzden
 * ulaşılmazdır. Hedefte
 * **zaten dolu** bir `tcKimlikNo` **farklı** bir dolu değerle **tek istekte** değiştirilemez →
 * `409 PERSONNEL_TC_ALREADY_SET` (mutasyon yapılmaz). Kendini hedefleme (route `userUuid`'i
 * çağıranın kendi `uuid`'iyle case-insensitive eşleşmesi) → `409 PERSONNEL_SELF_TARGET_FORBIDDEN`.
 * `emailOrPhone` **kasıtlı olarak bu istekte YOKTUR** — kimlik bilgisi §5.10 aktivasyon akışının
 * malıdır, bir roster düzenleme formunun değil (bir meslektaşının iletişim bilgisini
 * yeniden yazabilen Yetkili o hesap için parola sıfırlama başlatabilirdi).
 */
export interface SupplierPersonnelUpdateRequest {
  /** `null` = değişmez; asla `""` gönderilmez. */
  firstName: string | null;
  lastName: string | null;
  /** Anahtar yok = değişmez · `null` = temizle · dize = yaz. */
  tcKimlikNo?: string | null;
  accessLevel: ContractorAccessLevel;
  isActive: boolean;
}

/**
 * `PUT /supplier/contractor/personnel/{userUuid}` (**YENİ**, `update`, S26) — yukarıdaki
 * `SupplierPersonnelUpdateRequest`'i yazar; §13.3 `PUT`'unun (`accessLevel`/`isActive`) **üstüne**
 * `firstName`/`lastName`/`tcKimlikNo` de yazar (bu kayıtları supplier'ın kendisi oluşturur —
 * §13.6). Hedef kapsam dışı/`Invitation` → `404 PERSONNEL_NOT_FOUND`; `accessLevel` tanınmaz/
 * `firstName`\`lastName` boş dize → `400 VALIDATION_FAILED`; `tcKimlikNo` dolu ama geçersiz →
 * `400 TC_KIMLIK_INVALID`; kapsamda (kendisi hariç) başka birinde varsa →
 * `409 PERSONNEL_TC_ALREADY_EXISTS`; hedef config rolü tanımsız →
 * `409 ACCESS_LEVEL_ROLE_NOT_CONFIGURED` (mutasyon yapılmaz). **`accessLevel`/`isActive`
 * değişimi ancak bir sonraki access-token refresh'inde (≤15 dk) etkin olur** — arayüz anlık
 * kilitlenme vaat ETMEMELİDİR; `DELETE` (aşağıda) bunun aksine **anındadır**. →
 * `Result<ContractorPersonnelDto>` (güncel satır).
 */
export function updateSupplierContractorPersonnel(
  userUuid: string,
  body: SupplierPersonnelUpdateRequest,
) {
  return api.put<ContractorPersonnelDto>(`supplier/contractor/personnel/${userUuid}`, {
    json: body,
  });
}

/**
 * `DELETE /supplier/contractor/personnel/{userUuid}` (**YENİ**, `update`, S26) — kendi taşeron
 * firmasındaki bir personeli **bu firmadan çıkarır** (unbind): yalnız bu firma + kendi taşerona
 * ait `SupplierUserCompany` atama satırı **silinir**; `SupplierUser` kaydı, global rolleri,
 * başka firmalardaki atamaları **ve belgeleri korunur** (kayıt SİLİNMEZ, deaktive de
 * EDİLMEZ — bu ayrı bir kaldıraç, bkz. `updateSupplierContractorPersonnel`'in `isActive`'i).
 * **Ama bağ koptuğu an o kişinin belgeleri hem bu ekrandan hem İSG uzmanının §13.3 görünümünden
 * de kalkar** (her iki yol da hedefi aynı atamadan çözer) — kişi yeniden davet edilip aktifleşirse
 * **aynı `SupplierUserId` üzerinden geri gelir**. Kendini hedefleme (case-insensitive) →
 * `409 PERSONNEL_SELF_TARGET_FORBIDDEN`. Hedef kapsam dışı/`Invitation` → `404
 * PERSONNEL_NOT_FOUND`. **Anındadır** (bir sonraki istekte atama zaten yok — token'ın kendisi
 * iptal edilmez ama `SupplierScopeValidator` her istekte canlı atamayı doğrular). Eşzamanlı
 * "birbirini çıkarma" yarışına karşı kilit/sayım **yoktur** (S26 plan D8, kullanıcı kararı — bkz.
 * plan dosyası); kurtarma yolu ana firmanın `PUT .../personnel/{userUuid}` ile `accessLevel:
 * 'Yetkili'`'yi yeniden vermesidir. → `Result<Empty>`.
 */
export function deleteSupplierContractorPersonnel(userUuid: string) {
  return api.delete<Empty>(`supplier/contractor/personnel/${userUuid}`);
}

/**
 * `POST /supplier/contractor/personnel` (`update`) — GL6 §4.2. KIRICI: yanıt artık `CreatePersonnelResult`.
 * Contractor/firma kimliği istekte YOKTUR (§13.6): kişi aktif firmadaki `SupplierUserCompany.ContractorId`
 * bağından çözülen **kendi** taşerona eklenir. Bağ yoksa `404 CONTRACTOR_NOT_FOUND`; aktif firmaya atama
 * yoksa `403 COMPANY_NOT_ACCESSIBLE`; T.C. hataları `PERSONNEL_TC_*` kodlarıyla döner.
 */
export function createSupplierPersonnel(body: CreatePersonnelRequest) {
  return api.post<CreatePersonnelResult>('supplier/contractor/personnel', { json: body });
}

/** `POST /supplier/contractor/personnel/{userUuid}/activation-link` (`update`) — GL6 §4.3. */
export function sendSupplierActivationLink(userUuid: string, body: SendActivationLinkRequest) {
  return api.post<ActivationLinkResult>(
    `supplier/contractor/personnel/${userUuid}/activation-link`,
    { json: body },
  );
}

/** `DELETE /supplier/contractor/invitations/{invitationUuid}` (`update`) — GL6 §4.4. Kendi taşeronu dışı / bekleyen olmayan → `404 INVITATION_NOT_FOUND`. */
export function cancelSupplierInvitation(invitationUuid: string) {
  return api.delete<Empty>(`supplier/contractor/invitations/${invitationUuid}`);
}

/**
 * Supplier-scoped davet tekrar-gönderme isteği — api-contract §13.6
 * (`POST /supplier/contractor/personnel/invite/resend`). Yalnız `invitationUuid` + opsiyonel
 * `channel` taşır — **contractor/firma kimliği YOK** (§13.6 izolasyonu; backend kendi taşerona
 * kapsar). Guest resend'in (§5.10.4) `personnelUuid` alternatifi supplier tarafında **yoktur**
 * (kapsam kendi contractor'ıyla kapanır; davet satırında `invitationUuid` zaten eldedir).
 */
export interface SupplierInviteResendRequest {
  invitationUuid: string;
  /** Yalnız availableChannels iki elemanlıyken gönderilir. */
  channel?: InviteChannel | null;
}

/**
 * `POST /supplier/contractor/personnel/invite/resend` (`update`, GL6 §4.4 — route korunur,
 * semantik değişti) — supplier **kendi** taşeronuna ait bekleyen (`Pending`) bir aktivasyon
 * bağlantısını **tekrar gönderir**. Anahtar bağlantının `invitationUuid`'idir (kişi satırında
 * `pendingLink.invitationUuid`, düz davet satırında satır `uuid`'i). Backend saklı iletişimlerden
 * YENİ bir satır + yeni jeton üretir, eskisini iptal eder; yanıt `ActivationLinkResult` ve
 * içindeki YENİ `invitationUuid` eskisinin yerini alır (istemci eldeki uuid'i bununla değiştirir,
 * listeyi tazeler). `activationLink` **loglanmaz**. Contractor/firma kimliği GÖNDERİLMEZ (§13.6).
 *
 * Hatalar: `404 INVITATION_NOT_FOUND` (eksik / başka taşeronun / `Pending` olmayan — tek genel
 * kod); hedefli satırda `PERSONNEL_NOT_FOUND` / `PERSONNEL_ALREADY_HAS_LOGIN` /
 * `PERSONNEL_INACTIVE` / `PERSONNEL_TC_REQUIRED`; `400 INVITE_CHANNEL_UNAVAILABLE` / `NO_CONTACT`;
 * `429 ACTIVATION_LINK_COOLDOWN` / `ACTIVATION_LINK_DAILY_LIMIT`; bağ yok → `404
 * CONTRACTOR_NOT_FOUND`; atama yok → `403 COMPANY_NOT_ACCESSIBLE`. Hiçbir hatada satır yazılmaz.
 */
export function resendPersonnelInvite(body: SupplierInviteResendRequest) {
  return api.post<ActivationLinkResult>('supplier/contractor/personnel/invite/resend', {
    json: body,
  });
}

// ============================================================================
// 15.2 Supplier yetkili — kendi taşeronunun personel belgeleri (KVKK)
// `[ModuleAction("supplier-contractor", …)]`. Yol A (web-expert, §15.1) ile **aynı
// PersonnelDocumentDto shape**'i (contractors.ts kanonu) — yalnız hedef **kendi** contractor'ı
// (kimlik istemciden GÖNDERİLMEZ; token/bağdan çözülür, §13.6 gibi). `userUuid` supplier'ın
// kendi contractor'ına + firmasına atanmış olmalı (aksi `404 PERSONNEL_NOT_FOUND`).
// ============================================================================

/**
 * `GET /supplier/contractor/personnel/{userUuid}/documents` (`get`) — kendi taşeronundaki bir
 * personelin belgeleri (tür + geçerlilik + §8 dosya adı; sıra oluşturulma azalan). Bağ yoksa →
 * `404 CONTRACTOR_NOT_FOUND`; personel kapsam dışı → `404 PERSONNEL_NOT_FOUND`. Sayfasız dizi.
 */
export function listSupplierPersonnelDocuments(userUuid: string) {
  return api.get<PersonnelDocumentDto[]>(`supplier/contractor/personnel/${userUuid}/documents`);
}

/**
 * `POST /supplier/contractor/personnel/{userUuid}/documents` (`update`) — §8'de confirm edilmiş
 * dosyayı personele link'ler; yeni kimlik `value.uuid` (201). `documentUuid` confirmed + kendi
 * firması (yoksa `404 DOCUMENT_NOT_FOUND`); `type` tanınmaz / `documentUuid` boş →
 * `400 VALIDATION_FAILED`.
 */
export function linkSupplierPersonnelDocument(
  userUuid: string,
  body: PersonnelDocumentLinkRequest,
) {
  return api.post<PersonnelDocumentDto>(`supplier/contractor/personnel/${userUuid}/documents`, {
    json: body,
  });
}

/**
 * `DELETE /supplier/contractor/personnel/{userUuid}/documents/{linkUuid}` (`update`) — belge
 * linkini **soft-delete** eder.
 *
 * ⚠️ Silme hedefi **link kaydının kendi `uuid`'idir** (`PersonnelDocumentDto.uuid` ← entity.Uuid),
 * §8 storage `documentUuid`'i **DEĞİL** (backend `d => d.Uuid == …` ile çözer; ikisi ayrı GUID).
 * §8 `documentUuid` gönderilirse her silme `404 PERSONNEL_DOCUMENT_NOT_FOUND` döner (yol A'da bu
 * karışıklık 404'e yol açtı). İndirme ise §8 presigned GET ile `documentUuid` üzerinden yapılır.
 */
export function deleteSupplierPersonnelDocument(userUuid: string, linkUuid: string) {
  return api.delete<Empty>(`supplier/contractor/personnel/${userUuid}/documents/${linkUuid}`);
}

// ============================================================================
// 23.2 Araç kaydı (taşeron Yetkili) — `[ModuleAction("supplier-contractor", …)]`
// ============================================================================
//
// KAYNAK: api-contract.md §23.2. Taşeron istemciden ALINMAZ (§13.6). Personeli (`supplier-self`) → 403.
// Onay/red bu yolda YOKTUR. `vehicleUuid` = KAYIT aracı; belge silme hedefi `VehicleDocumentDto.uuid`.

/** `GET /supplier/contractor/vehicles` (`get`). */
export function listSupplierVehicles() {
  return api.get<ContractorVehicleDto[]>('supplier/contractor/vehicles');
}

/** `POST /supplier/contractor/vehicles` (`update`) → `201`; `409 VEHICLE_PLATE_ALREADY_EXISTS`. */
export function createSupplierVehicle(body: VehicleWriteRequest) {
  return api.post<ContractorVehicleDto>('supplier/contractor/vehicles', { json: body });
}

/** `PUT /supplier/contractor/vehicles/{vehicleUuid}` (`update`) — tam-replace; taşeron plaka kilidi kuralıyla. */
export function updateSupplierVehicle(vehicleUuid: string, body: VehicleWriteRequest) {
  return api.put<ContractorVehicleDto>(`supplier/contractor/vehicles/${vehicleUuid}`, {
    json: body,
  });
}

/** `DELETE /supplier/contractor/vehicles/{vehicleUuid}` (`update`) — `409 VEHICLE_HAS_OPEN_PERMITS`. */
export function deleteSupplierVehicle(vehicleUuid: string) {
  return api.delete<Empty>(`supplier/contractor/vehicles/${vehicleUuid}`);
}

/** `POST …/{vehicleUuid}/documents` (`update`) → `201`; belge `Pending` başlar. */
export function linkSupplierVehicleDocument(
  vehicleUuid: string,
  body: VehicleDocumentUploadRequest,
) {
  return api.post<VehicleDocumentDto>(`supplier/contractor/vehicles/${vehicleUuid}/documents`, {
    json: body,
  });
}

/** `DELETE …/{vehicleUuid}/documents/{did}` (`update`) — yalnız `Pending`; aksi `409 VEHICLE_DOCUMENT_NOT_PENDING`. */
export function deleteSupplierVehicleDocument(vehicleUuid: string, documentUuid: string) {
  return api.delete<Empty>(`supplier/contractor/vehicles/${vehicleUuid}/documents/${documentUuid}`);
}
